import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import useOnKeyPressIfFocused from '../../../../../hooks/useOnKeyPressIfFocused';
import useSearchParams from '../../../../../hooks/useSearchParams';
import { sleep } from '../../../../../utils/generics';
import { inputCodeWithoutZeroWidthSpace } from '../../../../../utils/regexes';
import useDebounceFunction from '../../../../Hooks/useDebouncedFunction';
import {
  CleanFilter,
  CommonFilterHookReturn,
  GetSearchParamKeys,
  HandleResetToDefaultValue,
} from '../commonHookReturn.types';
import { MILLISECONDS_TO_APPLY_FILTER } from '../constants';

interface UseTextFilterParams {
  key: string;
  defaultValue?: string;
  shouldIgnoreDefaultFilters?: boolean;
  isAutoApply?: boolean;
}

interface UseTextFilterOutput extends CommonFilterHookReturn {
  key: string;
  textFieldRef: RefObject<HTMLInputElement>;
  filteredValue: string;
  hasSelectedValue: boolean;
  value: string;
  setValue: (newValue: string) => void;
}

const useTextFilter = ({
  key,
  defaultValue,
  isAutoApply,
  shouldIgnoreDefaultFilters = true,
}: UseTextFilterParams): UseTextFilterOutput => {
  const { setSearchParams, removeSearchParams, searchParams } = useSearchParams();

  const getDefaultValue = () => {
    const userDefaultValue = shouldIgnoreDefaultFilters ? searchParams[key] : defaultValue;
    const normalizedDefaultValue = userDefaultValue ?? '';
    return normalizedDefaultValue;
  };

  const textFieldRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(getDefaultValue);
  const [filteredValue, setFilteredValue] = useState(getDefaultValue);

  const applyFilterInSearchParams = useCallback(
    (query: string) => {
      if (query) {
        setSearchParams({ [key]: query });

        return;
      }
      removeSearchParams([key]);
    },
    [key, setSearchParams, removeSearchParams],
  );

  const handleApplyFilterInSearchParams = useCallback(async () => {
    await sleep(30);
    const input = textFieldRef.current;
    if (!input) {
      return;
    }
    const query = inputCodeWithoutZeroWidthSpace(input.value);
    input.value = query;

    setValue(query);
    applyFilterInSearchParams(query);
    setFilteredValue(query);
  }, [applyFilterInSearchParams]);
  useOnKeyPressIfFocused({
    eventKey: 'Enter',
    ref: textFieldRef,
    onPress: handleApplyFilterInSearchParams,
  });

  const cleanFilter: CleanFilter = () => {
    setValue('');
    applyFilterInSearchParams('');
    setFilteredValue('');
  };

  const getSearchParamKeys: GetSearchParamKeys = () => key;

  const handleResetToDefaultValue: HandleResetToDefaultValue = () => {
    if (!defaultValue) {
      cleanFilter();
      return;
    }
    const normalizedDefaultValue = defaultValue ?? '';
    setValue(normalizedDefaultValue);
    applyFilterInSearchParams(normalizedDefaultValue);
    setFilteredValue(normalizedDefaultValue);
  };
  const { handleDebounceFunction } = useDebounceFunction({
    debounceTime: MILLISECONDS_TO_APPLY_FILTER,
  });

  useEffect(() => {
    if (isAutoApply) {
      handleDebounceFunction(handleApplyFilterInSearchParams);
      return;
    }
    const hasClearedAppliedValue = !value && !!filteredValue;
    if (hasClearedAppliedValue) {
      cleanFilter();
    }
  }, [value]);

  return {
    key,
    textFieldRef,
    value,
    filteredValue,
    hasSelectedValue: !!filteredValue,
    setValue: (newValue: string) => setValue(newValue),
    cleanFilter,
    getSearchParamKeys,
    handleResetToDefaultValue,
  };
};

export default useTextFilter;
